import { useState } from 'react';
import { Plus, Trash2, ExternalLink } from 'lucide-react';

const INP = {
  width: '100%', padding: '10px 13px', borderRadius: 5,
  background: 'rgba(255,255,255,.05)', border: '1px solid rgba(201,168,76,.18)',
  color: '#fff', fontSize: '.84rem', fontFamily: 'var(--sans)',
  outline: 'none', boxSizing: 'border-box',
};
const LBL = {
  fontSize: '.6rem', color: 'rgba(255,255,255,.36)',
  letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 5, display: 'block',
};

function ImagePreview({ url }) {
  if (!url) return null;
  return (
    <div style={{
      marginTop: 8, borderRadius: 6, overflow: 'hidden',
      border: '1px solid rgba(201,168,76,.2)', height: 90,
    }}>
      <img src={url} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        onError={e => { e.target.style.opacity = '.2'; }} />
    </div>
  );
}

export default function Step5Media({ form, update }) {
  const [galleryInput, setGalleryInput] = useState('');

  const addGalleryUrl = () => {
    const url = galleryInput.trim();
    if (!url || form.gallery.includes(url)) { setGalleryInput(''); return; }
    update({ gallery: [...form.gallery, url] });
    setGalleryInput('');
  };

  const removeGallery = (i) => update({ gallery: form.gallery.filter((_, idx) => idx !== i) });

  const sectionHead = {
    color: 'rgba(255,255,255,.85)', fontSize: '.82rem', fontWeight: 700,
    marginBottom: '1rem', marginTop: '.5rem',
    paddingBottom: '.5rem', borderBottom: '1px solid rgba(201,168,76,.1)',
    display: 'flex', alignItems: 'center', gap: 7,
  };

  return (
    <div>
      <div style={sectionHead}>
        <span style={{ color: 'var(--gold)' }}>⑤</span> Media & Assets
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.85rem 1rem' }}>
        {/* Cover Image */}
        <div>
          <label style={LBL}>Cover Image URL *</label>
          <input id="step5-cover-image" name="coverImage" style={INP} value={form.coverImage}
            onChange={e => update({ coverImage: e.target.value })}
            placeholder="https://cdn.example.com/cover.jpg" />
          <ImagePreview url={form.coverImage} />
        </div>

        {/* Master Plan */}
        <div>
          <label style={LBL}>Master Plan Image URL</label>
          <input id="step5-master-plan" name="masterPlan" style={INP} value={form.masterPlan}
            onChange={e => update({ masterPlan: e.target.value })}
            placeholder="https://cdn.example.com/masterplan.jpg" />
          <ImagePreview url={form.masterPlan} />
        </div>

        {/* Hero Video */}
        <div>
          <label style={LBL}>Hero Video URL</label>
          <input id="step5-hero-video" name="heroVideo" style={INP} value={form.heroVideo}
            onChange={e => update({ heroVideo: e.target.value })}
            placeholder="https://youtube.com/embed/xxx" />
          {form.heroVideo && (
            <div style={{ fontSize: '.68rem', color: 'rgba(201,168,76,.5)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
              <ExternalLink size={10} />
              <a href={form.heroVideo} target="_blank" rel="noreferrer"
                style={{ color: 'inherit', textDecoration: 'none' }}>Preview link</a>
            </div>
          )}
        </div>

        {/* Brochure */}
        <div>
          <label style={LBL}>PDF Brochure URL</label>
          <input id="step5-brochure" name="brochure" style={INP} value={form.brochure}
            onChange={e => update({ brochure: e.target.value })}
            placeholder="https://cdn.example.com/brochure.pdf" />
          {form.brochure && (
            <div style={{ fontSize: '.68rem', color: 'rgba(201,168,76,.5)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
              <ExternalLink size={10} />
              <a href={form.brochure} target="_blank" rel="noreferrer"
                style={{ color: 'inherit', textDecoration: 'none' }}>View PDF</a>
            </div>
          )}
        </div>
      </div>

      {/* Gallery */}
      <div style={{ marginTop: '1.2rem' }}>
        <label style={LBL}>Image Gallery ({form.gallery.length} image{form.gallery.length !== 1 ? 's' : ''})</label>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          <input
            id="step5-gallery-input"
            name="galleryInput"
            style={{ ...INP, flex: 1 }}
            value={galleryInput}
            onChange={e => setGalleryInput(e.target.value)}
            placeholder="Paste image URL and press Enter"
            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addGalleryUrl())}
          />
          <button onClick={addGalleryUrl} style={{
            background: 'rgba(201,168,76,.1)', border: '1px solid rgba(201,168,76,.25)',
            borderRadius: 5, padding: '9px 14px', color: 'var(--gold)',
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
            fontSize: '.78rem', fontFamily: 'var(--sans)',
          }}>
            <Plus size={13} /> Add
          </button>
        </div>

        {form.gallery.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px,1fr))', gap: 6 }}>
            {form.gallery.map((url, i) => (
              <div key={i} style={{ position: 'relative', aspectRatio: '1', borderRadius: 5, overflow: 'hidden', border: '1px solid rgba(201,168,76,.15)' }}>
                <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={e => { e.target.style.opacity = '.3'; }} />
                <button onClick={() => removeGallery(i)} style={{
                  position: 'absolute', top: 2, right: 2, background: 'rgba(0,0,0,.75)',
                  border: 'none', borderRadius: '50%', width: 18, height: 18,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', color: '#fff',
                }}>
                  <Trash2 size={9} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
